import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";

import { useNotificationNamek } from "@/features/notifications";
import { INamekError } from "@/models";
import {
  DescriptionUnknownError,
  SaveErrorMessage,
  SaveSuccessTitle,
} from "@/constants/commonMessages";
import {
  type MaterialItem,
  type GreenCenterMaterials,
  materialQueryKeys,
  useGetMaterialsByCVId,
} from "@/service/namek/useGetMaterialsByCVId";
import { useRefetchMaterialsList } from "@/config/hooks/useRefetchMaterialsList";

import { useRefetchAssignMaterialsList } from "../../hooks/useRefetchAssignMaterialsList";
import { useUpdateAssignMaterials } from "../../services/useUpdateAssignMaterials";

import {
  AssignMaterialFormSchema,
  type AssignMaterialFormValues,
} from "./AssignMaterialForm.schema";

type UseAssignMaterialFormArgs = {
  id: number;
  onDone: () => void;
  onSave?: (selected: MaterialItem[], assignMaterial: GreenCenterMaterials) => void;
};

export const useAssignMaterialForm = ({ id, onDone, onSave }: UseAssignMaterialFormArgs) => {
  const { toast } = useNotificationNamek();
  const { refetchList: refetchAssignMaterials } = useRefetchAssignMaterialsList();
  const { refetchList: refetchMaterialsList } = useRefetchMaterialsList();
  const updator = useUpdateAssignMaterials();

  const {
    data: assignMaterial,
    isFetching,
    error,
  } = useGetMaterialsByCVId(materialQueryKeys.list({ greenCenterId: id }), id);

  useEffect(() => {
    if (!error) return;

    if (error.status === 404) {
      toast.error("No se puede editar la asignación", {
        description: "El registro no existe o fue eliminado del listado.",
        actionButton: {
          label: "Actualizar listado",
          onClick: () => {
            refetchMaterialsList();
            refetchAssignMaterials();
          },
        },
      });
    } else {
      toast.error("No se puede editar la asignación", {
        description: DescriptionUnknownError,
      });
    }

    onDone();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, onDone]);

  const form = useForm<AssignMaterialFormValues>({
    resolver: zodResolver(AssignMaterialFormSchema),
    defaultValues: {
      materials: assignMaterial?.materials ?? [],
    },
  });

  useEffect(() => {
    if (!assignMaterial) return;
    form.reset({ materials: assignMaterial.materials ?? [] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignMaterial?.id, assignMaterial?.materials?.length]);

  const handleError = (error: INamekError) => {
    if (error.status === 404) {
      toast.error(SaveErrorMessage.title, {
        description: "El registro no existe o fue eliminado del listado.",
        actionButton: {
          label: "Actualizar listado",
          onClick: () => {
            refetchMaterialsList();
            refetchAssignMaterials();
          },
        },
      });
      onDone();
      return;
    }

    toast.show({
      ...SaveErrorMessage,
      type: "error",
    });
  };

  const onSubmit = useCallback(
    (data: AssignMaterialFormValues) => {
      if (!assignMaterial) return;
      const materialIds = (data.materials ?? []).map((m) => m.id);
      updator.mutate(
        { id: assignMaterial.id, materialIds },
        {
          onSuccess: () => {
            toast.success(SaveSuccessTitle);
            onSave?.(data.materials, assignMaterial);
            onDone();
          },
          onError: (error) => handleError(error),
        },
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onDone, onSave, assignMaterial, updator],
  );

  return {
    assignMaterial,
    form,
    onSubmit,
    isFetching,
    isPending: updator.isPending,
  };
};
